<template>
  <div class="position-relative mb-4">
    <div
      v-if="data.typeID !== 2"
      class="d-flex align-items-center"
      style="cursor: pointer"
      @click="openBox"
    >
      <div>
        <h4 class="mb-1">
          <template v-if="data.localityName">
            {{ data.localityName }}
          </template>
          <template v-else>
            {{ data.powerNumber }}
          </template>
        </h4>
        <div>
          <span
            >{{ data.street }} {{ data.streetNumber }}, {{ data.postCode }}
            {{ data.city }}</span
          >
          <b-badge v-if="data.locality" class="ml-1 fs-sm font-weight-normal">
            {{ data.locality }}
          </b-badge>
        </div>
      </div>
      <div class="ml-auto">
        <transition name="fadeShort">
          <div
            v-if="Object.keys(currentWeather).length"
            class="d-flex align-items-center justify-content-center"
          >
            <div class="my-n2">
              <img
                :src="`/images/weather/animated/${weatherIcons[currentWeather.condition.iconId]}.svg`"
                style="width: 75px"
              />
            </div>
            <h4 class="h2 mb-0 text-nowrap">
              {{ currentWeather.temperature | formatNumber }} °C
            </h4>
            <b-icon class="ml-3" font-scale="1.25x" icon="caret-down" />
          </div>
        </transition>
      </div>
    </div>
    <transition name="fadeUpShort">
      <b-card
        v-if="open"
        class="card-shadow position-absolute"
        no-body
        no-header
        style="top: 0; right: 0; left: 0; z-index: 10"
      >
        <div
          class="card-body p-0"
          style="
            background-image: url('/images/weather/cloudy.jpg');
            background-size: cover;
          "
        >
          <div class="gradient-dark position-relative p-4">
            <div
              v-if="data.typeID !== 2"
              class="position-absolute"
              style="top: 0; right: 0"
            >
              <b-btn variant="link" class="text-white" @click="closeBox">
                <b-icon icon="x" font-scale="2x" />
              </b-btn>
            </div>
            <div class="d-flex align-items-center">
              <div>
                <h4 class="mb-1 text-white">
                  <template v-if="data.localityName">
                    {{ data.localityName }}
                  </template>
                  <template v-else>
                    {{ data.powerNumber || data.gasNumber }}
                  </template>
                </h4>
                <div class="text-white">
                  <span
                    >{{ data.street }} {{ data.streetNumber }},
                    {{ data.postCode }} {{ data.city }}</span
                  >
                  <b-badge
                    v-if="data.locality"
                    variant="white"
                    class="ml-1 fs-sm font-weight-normal"
                  >
                    {{ data.locality }}
                  </b-badge>
                </div>
              </div>
              <div class="ml-auto" />
            </div>
          </div>
          <transition mode="out-in" name="fadeDownShort">
            <div
              v-if="!historyView && Object.keys(currentWeather).length"
              key="classicView"
            >
              <div class="px-4 mt-n4">
                <div class="d-flex align-items-center justify-content-center">
                  <div class="mt-n2">
                    <img
                      v-if="currentWeather.condition"
                      :src="`/images/weather/animated/${weatherIcons[currentWeather.condition.iconId]}.svg`"
                      style="width: 150px"
                    />
                  </div>
                  <div class="text-white">
                    <h4
                      class="text-white text-nowrap mb-0"
                      style="
                        font-size: 40px;
                        text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.35);
                      "
                    >
                      {{ currentWeather.temperature | formatNumber }} °C
                    </h4>
                    <div class="text-uppercase">
                      {{ currentWeather.condition.description }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="text-center text-uppercase text-white mt-n2">
                <small
                  >Aktualizované:
                  {{ currentWeather.dateTime | formatTime('HH:mm') }}</small
                >
              </div>
              <b-row
                align-v="center"
                class="text-white text-uppercase fs-sm mb-2 mx-0"
              >
                <b-col class="text-center">
                  Pocitová teplota
                  <b-badge variant="white" class="fs-sm">
                    {{ currentWeather.feelsLikeTemperature | formatNumber }} °C
                  </b-badge>
                </b-col>
                <b-col class="text-center">
                  Vietor
                  <b-badge variant="white" class="fs-sm">
                    {{ currentWeather.windSpeed | formatNumber(1) }} km/h
                  </b-badge>
                </b-col>
                <b-col class="text-center">
                  Vlhkosť
                  <b-badge variant="white" class="fs-sm">
                    {{ currentWeather.humidity | formatNumber }} %
                  </b-badge>
                </b-col>
                <b-col class="text-center">
                  Barometer
                  <b-badge variant="white" class="fs-sm">
                    {{ currentWeather.pressure | formatNumber }} mbar
                  </b-badge>
                </b-col>
              </b-row>
              <div class="d-flex justify-content-center text-white mb-3 mx-0">
                <div
                  v-for="(item, i) in hourlyForecast"
                  :key="i"
                  class="text-center"
                  :class="{ 'mr-2 pr-2 border-right': i < 3 }"
                >
                  {{ item.dateTime | formatTime('HH:mm') }}
                  <img
                    :src="`/images/weather/animated/${weatherIcons[item.condition.iconId]}.svg`"
                    style="width: 30px"
                  />
                  {{ item.temperature | formatNumber }} °C
                </div>
              </div>
              <hr />
              <b-row class="d-flex text-white mb-3 mx-3">
                <div
                  v-b-tooltip.hover.right
                  class="d-flex align-self-stretch text-center pr-3"
                  style="
                    width: 50px;
                    border-right: 1px solid rgba(255, 255, 255, 0.5);
                    cursor: pointer;
                  "
                  title="História počasia"
                  @click="openHistory"
                >
                  <b-icon
                    class="align-self-center"
                    icon="clock-history"
                    font-scale="2x"
                  />
                </div>
                <b-col
                  v-for="(daily, d) in dailyForecast"
                  :key="d"
                  class="text-center"
                >
                  <div class="fs-sm">
                    {{
                      $moment(daily.date, 'YYYY-MM-DD')
                        .locale('sk')
                        .format('ddd DD.MM')
                    }}
                  </div>
                  <img
                    :src="`/images/weather/static/${weatherIcons[daily.condition.iconId]}.svg`"
                    class="my-n2"
                  />
                  <div>
                    {{ daily.temperatureDay | formatNumber }} /
                    <small
                      >{{ daily.temperatureNight | formatNumber }} °C</small
                    >
                  </div>
                </b-col>
              </b-row>
            </div>
            <div
              v-if="historyView"
              key="historyView"
              class="p-3"
              style="background-color: rgba(255, 255, 255, 0.75)"
            >
              <div class="d-flex align-items-center mb-2">
                <div class="d-flex align-items-center">
                  <b-btn
                    size="sm"
                    variant="outline-secondary"
                    class="mr-2"
                    @click="closeHistory"
                  >
                    <b-icon icon="arrow-left" />
                  </b-btn>
                  <h4 class="mb-0">História počasia</h4>
                </div>
                <div class="ml-auto">
                  <b-btn
                    class="ml-1"
                    size="sm"
                    variant="secondary"
                    @click="exportWeather"
                  >
                    <b-icon class="mr-1" icon="file-earmark-spreadsheet" />
                    Exportovať
                  </b-btn>
                </div>
              </div>
              <!--              <b-row
                align-v="center"
                class="my-4"
              >
                <b-col sm="4">
                  <div class="text-center">
                    <div>Najnižšia teplota</div>
                    <hr class="my-1">
                    <h4 class="m-0">
                      4 °C
                    </h4>
                  </div>
                </b-col>
                <b-col sm="4">
                  <div class="text-center">
                    <div>Najvyššia teplota</div>
                    <hr class="my-1">
                    <h4 class="m-0">
                      16 °C
                    </h4>
                  </div>
                </b-col>
                <b-col sm="4">
                  <div class="text-center">
                    <div>Priemerná teplota</div>
                    <hr class="my-1">
                    <h4 class="m-0">
                      11 °C
                    </h4>
                  </div>
                </b-col>
              </b-row>-->
              <server-table
                ref="table"
                :api-request="apiWeather"
                :busy.sync="table.loading"
                :columns="table.columns"
                :sort-by.sync="table.sortBy"
                :searchable="false"
                borderless
                small
                table-class="bg-transparent"
              >
                <template #cell(text)="data">
                  <div class="d-flex align-items-center overflow-hidden">
                    <img
                      :src="`/images/weather/static/${weatherIcons[data.item.condition.iconId]}.svg`"
                      class="my-n4"
                      style="width: 45px"
                    />
                    <div>{{ data.item.condition.description }}</div>
                  </div>
                </template>
              </server-table>
            </div>
          </transition>
        </div>
      </b-card>
    </transition>
  </div>
</template>
<script>
import ServerTable from '@/components/ServerTable'
import operationService from '@/services/api/operationService'
import { mapState } from 'vuex'
import getPeriod from '../mixins/getPeriod'

export default {
  components: {
    ServerTable,
  },
  mixins: [getPeriod],
  data() {
    return {
      open: false,
      historyView: false,
      weatherIcons: {
        1: 'day',
        2: 'cloudy-day-1',
        3: 'cloudy-day-3',
        4: 'cloudy',
        9: 'rainy-3',
        10: 'rainy-6',
        11: 'thunder',
        13: 'snowy-6',
        50: 'cloudy',
      },
      currentWeather: {},
      dailyForecast: [],
      hourlyForecast: [],
      table: {
        loading: false,
        columns: [
          {
            key: 'date',
            label: 'Dátum',
            formatter: (value) =>
              value ? this.$options.filters.formatDate(value) : '-',
            sortable: true,
          },
          {
            key: 'temperatureDay',
            label: 'Teplota',
            class: 'text-right',
            tdClass: 'text-nowrap',
            formatter: (value) =>
              value ? this.$options.filters.formatNumber(value) + ' °C' : '-',
            sortable: true,
          },
          {
            key: 'temperatureMin',
            label: 'Min.',
            class: 'text-right',
            tdClass: 'text-nowrap',
            formatter: (value) =>
              value ? this.$options.filters.formatNumber(value) + ' °C' : '-',
            sortable: true,
          },
          {
            key: 'temperatureMax',
            label: 'Max.',
            class: 'text-right',
            tdClass: 'text-nowrap',
            formatter: (value) =>
              value ? this.$options.filters.formatNumber(value) + ' °C' : '-',
            sortable: true,
          },
          {
            key: 'text',
            label: 'Počasie',
            thStyle: 'width:65%',
            tdClass: 'text-nowrap',
          },
        ],
        sortBy: 'date',
      },
    }
  },
  created() {
    this.getCurrentWeather()
    this.getHourlyForecast()
    this.getDailyForecast()
  },
  computed: {
    ...mapState({
      data: (state) => state.energetic.current.data,
    }),
  },
  mounted() {
    if (this.data.typeID === 2) {
      this.open = true
    }
  },
  methods: {
    openBox() {
      if (this.data.typeID !== 2) {
        this.open = true
      }
    },
    closeBox() {
      this.open = false
      this.closeHistory()
    },
    openHistory() {
      this.historyView = true
    },
    closeHistory() {
      this.historyView = false
    },
    getCurrentWeather() {
      operationService
        .getCurrentWeatherByDeliveryPoint(
          this.$store.getters['operation/get'].callback_id,
          {
            dateTime: {
              ge: this.$moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
              le: this.$moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
            },
          },
        )
        .then((res) => {
          this.currentWeather = res.data.length ? res.data.slice(-1)[0] : {}
        })
    },
    getHourlyForecast() {
      operationService
        .getHourlyForecastByDeliveryPoint(
          this.$store.getters['operation/get'].callback_id,
          {
            dateTime: {
              ge: this.$moment().format('YYYY-MM-DD HH:00:00'),
              le: this.$moment().add(1, 'day').format('YYYY-MM-DD HH:00:00'),
            },
          },
        )
        .then((res) => {
          let data = res.data
          let hours = []
          for (let h of [6, 12, 18, 24]) {
            hours.push(
              this.$moment().add(h, 'hours').format('YYYY-MM-DD HH:00'),
            )
          }
          this.hourlyForecast = data.filter((item) => {
            for (let h of hours) {
              if (h == item.dateTime) {
                return true
              }
            }
          })
        })
    },
    getDailyForecast() {
      operationService
        .getDailyForecastByDeliveryPoint(
          this.$store.getters['operation/get'].callback_id,
          {
            date: {
              ge: this.$moment()
                .add(1, 'day')
                .startOf('day')
                .format('YYYY-MM-DD'),
              le: this.$moment().add(5, 'day').format('YYYY-MM-DD'),
            },
          },
        )
        .then((res) => {
          this.dailyForecast = res.data
        })
    },
    apiWeather(params) {
      return operationService
        .getDailyForecastByDeliveryPoint(
          this.$store.getters['operation/get'].callback_id,
          {
            orderBy: params.orderBy,
            maxResults: params.maxResults,
            startAt: params.startAt,
            date: { ...this.period.from, ...this.period.to },
          },
        )
        .then((res) => {
          res.data = {
            filtered: params.maxResults,
            total: res.headers['x-pagination-total-count']
              ? res.headers['x-pagination-total-count']
              : 0,
            rows: res.data,
          }

          return res
        })
    },
    exportWeather() {
      this.$swal({
        toast: true,
        type: 'info',
        position: 'top-end',
        title: 'Pripravujem export...',
      })
      operationService
        .getWeatherHourlyExcelExportByDeliveryPointId(
          this.$store.getters['operation/get'].callback_id,
          {
            dateFrom: this.period.from.ge,
            dateTo: this.period.to.le,
          },
        )
        .then((res) => {
          this.$swal({
            toast: true,
            type: 'success',
            position: 'top-end',
            title: 'Export pripravený',
            timer: 5000,
          })
        })
        .catch(() => {
          this.$swal({
            toast: true,
            type: 'error',
            position: 'top-end',
            title: 'Chyba pri exportovaní',
            timer: 5000,
          })
        })
    },
  },
}
</script>
